import { PixiController } from 'app/services/PixiController';
import { DepartmentType } from 'app/data/Enums';
import { WorkTilePixi } from 'app/module/work/WorkTilePixi';
import { ProjectConfig } from 'app/model/ProjectConfig';
import { device } from 'utils/device';
import { WorkSceneDeps } from './work.types';

/**
 * Pixi side of the work section: the work container, spritesheets, the
 * opening background sprite and the carousel tiles (creation, per-frame
 * timeline progress, hit areas, hide/show/reset passes).
 */
export class WorkScene {
	private _deps: WorkSceneDeps;
	private _pixiController: PixiController;

	//Tile vars:
	private _tiles: Array<WorkTilePixi>;
	private _tileRatioShare: number;
	private _actualProgress: number;
	private _reduce: number;
	private _step: number;
	private _finalStep: number;

	private _introActiveRange: number;

	private _activeProjectTile: WorkTilePixi;
	private _activeProjectIndex: number = 0;

	private _$workContainer: PIXI.Container;
	private _particlesSpritesheet: PIXI.Spritesheet;
	private _particlesSpritesheet32: PIXI.Spritesheet;

	private _backgroundSpritesheet: PIXI.Spritesheet;
	private _backgroundSpritesheetLite: PIXI.Spritesheet;


	private _openingBg: PIXI.Sprite;

	private _needsHardSizeReset: boolean = false;

	private readonly INNER_TILE_STEP: number = 0.5;

	private processing = false;

	constructor(deps: WorkSceneDeps) {
		this._deps = deps;
	}

	//Accessors
	//-------------------------------------------------------------------------------------

	public get tiles(): Array<WorkTilePixi> {
		return this._tiles;
	}

	public get workContainer(): PIXI.Container {
		return this._$workContainer;
	}

	public get tileRatioShare(): number {
		return this._tileRatioShare;
	}

	public get introActiveRange(): number {
		return this._introActiveRange;
	}

	public get activeTile(): WorkTilePixi {
		return this._activeProjectTile;
	}

	public set activeTile(tile: WorkTilePixi) {
		this._activeProjectTile = tile;
	}

	public get activeProjectIndex(): number {
		return this._activeProjectIndex;
	}

	public set activeProjectIndex(index: number) {
		this._activeProjectIndex = index;
	}

	public setPixiController(pixi: PixiController): void {
		this._pixiController = pixi;
	}

	public requestHardSizeReset(): void {
		this._needsHardSizeReset = true;
	}

	//Pixi dependecies (set in app controller via WorkController):
	public setPixi(
		$pixi: PIXI.Application,
		$backgroundContainer: PIXI.Container,
		$workContainer: PIXI.Container,
		isDeepLink: boolean,
		department: DepartmentType
	): void {

		if ($workContainer) {
			this._$workContainer = $workContainer;
			this._$workContainer.visible = false;
			this._$workContainer.renderable = false;
			this._$workContainer.name = department;

			if (!isDeepLink) {
				this._$workContainer.alpha = 0;
			} else {
				this._$workContainer.renderable = true;
				this._$workContainer.visible = true;
			}
		}
	}

	//Spritesheets
	//-------------------------------------------------------------------------------------

	public setParticlesSpritesheet(spritesheet: PIXI.Spritesheet): void {
		this._particlesSpritesheet = spritesheet;
	}

	public setParticlesSpritesheet32(spritesheet: PIXI.Spritesheet): void {
		this._particlesSpritesheet32 = spritesheet;
	}

	public setBackgroundSpritesheet(spritesheet: PIXI.Spritesheet): void {
		this._backgroundSpritesheet = spritesheet;
	}

	public setBackgroundSpritesheetLite(spritesheet: PIXI.Spritesheet): void {
		this._backgroundSpritesheetLite = spritesheet;
	}

	public setMothSpritesheet(spritesheet: PIXI.Spritesheet): void {
	}

	public hasRequiredSpritesheets(): boolean {
		return !!(this._particlesSpritesheet && this._particlesSpritesheet32 && this._backgroundSpritesheet);
	}

	//Creation
	//-------------------------------------------------------------------------------------

	public createFirstSlideBG() {
		if (!this._$workContainer) {
			return;
		}

		let texture = this._backgroundSpritesheet.textures['opening-bg.jpg'];

		if (texture) {
			this._openingBg = new PIXI.Sprite();
			this._openingBg.texture = texture;
			this._$workContainer.addChild(this._openingBg);
		}
	}

	/** Create all the caoursel tiles: */
	public createTiles(projects: Array<ProjectConfig>, container: HTMLElement): void {
		let tileIndex: number;
		this._tiles = [];

		//Add a blank spacer for the intro tile
		let tile: WorkTilePixi = new WorkTilePixi(null, null, null, null, null);
		this._tiles.push(tile);

		//Add the main set
		projects.forEach((project: ProjectConfig, index: number) => {
			//Allow for blank spacer in indexing logic:
			tileIndex = index;
			//Create the tile
			let tile: WorkTilePixi = new WorkTilePixi(this._pixiController, tileIndex, project, this._$workContainer, container);
			tile.setSpriteSheets(
				this._particlesSpritesheet,
				this._backgroundSpritesheet,
				this._backgroundSpritesheetLite,
				this._particlesSpritesheet32
			);
			tile.setHitarea(this._deps.getHitArea(index));

			//Explore from holding down on project tile:
			tile.onExploreProject = (data) => this._deps.onExploreProject(data);

			tile.create();
			this._tiles.push(tile);
		});

		//Need to work out how much each tile share takes up
		//The caveat here is that Tile 1 (index 0) is on 100% width like the others
		//So we need to correct that share of progress

		this._tileRatioShare = 1.0 / this._tiles.length;

		//Set the intro amount for calulating the animation of that HTMLElement
		this._introActiveRange = 1 / this._tiles.length;
	}

	//Per-frame updates
	//-------------------------------------------------------------------------------------

	/** Update each tile progress based on the */
	public updateTileProgress(tileProgress: number, velocity: number, forceUpdate = false): void {
		//Progress could be <0.0 or >1.0 so get the decimal part:

		this._actualProgress = tileProgress % 1;
		this._reduce = (this._actualProgress / this._tileRatioShare) * this.INNER_TILE_STEP;

		let firstTileSkewX = 0;

		//We need to cut off 15% of one tiles
		//So AT 0% then the

		//Each tile is active bewteen its share of the ratio:

		let i;

		for (i = 0; i < this._tiles.length; i++) {
			//progress = 0.1
			//item1 progress = 0.5
			//item 2 progress = 1.0
			//item 5 progress = 0.0
			//ratio = 0.3 then item 2 has progress = 0.5 and item 1 has progress = 0

			////ratio = 0.3 then item 2 has progress = 0.5 and item 1 has progress = 0
			this._step = (i + 1) * this.INNER_TILE_STEP;

			this._finalStep = this._step - this._reduce;

			if (i == 1) {
				this._finalStep -= firstTileSkewX;
			}

			let progress = this._tiles[i].setTimelineProgress(this._finalStep, false, velocity, forceUpdate);

			//Get a list of update props for each partcile:
			if (progress !== null) {
			}
		}

		//Update the worker with the data

		if (this.processing) {
			return;
		}
	}


	/** Move the main background along the */
	public updateBGAnimation(tileProgress: number, innerWidth: number): void {
		if (!this._deps.isActive()) {
			return;
		}

		//Allow for the intro in the calulations
		//Intro takes up tile slide share of the movement
		this._actualProgress = tileProgress % 1;

		let openBGTextureWidth = 1660;
		let openingBGTextureHeight = 1011;
		let openingBgTextTureRotation = 0;
		let buffer = 0.05; //add 10% space bufer around the image
		let totalSizeRatio = 1 - buffer;

		if (device.isPortraitDevice) {
			openingBgTextTureRotation = 90;
			openBGTextureWidth = 1011;
			openingBGTextureHeight = 1660;
		}

		//Scale to contain logic:
		let rw = Math.min(1, device.innerWidth / (openBGTextureWidth * totalSizeRatio));
		let rh = Math.min(1, device.innerHeight / (openingBGTextureHeight * totalSizeRatio));
		let scale = 1;

		if (rw < 1 || rh < 1) {
			scale = rw < rh ? rw : rh;
		}

		this._openingBg.scale.set(scale, scale);

		let moveWidth = this._openingBg.width;
		let moveHeight = this._openingBg.height;
		let moveX = 0;

		if (openingBgTextTureRotation == 90 || openingBgTextTureRotation == 270) {
			moveWidth = this._openingBg.height;
			moveHeight = this._openingBg.width;
			moveX = moveWidth;
		}

		//Set the rotation (90 Degrees when protrait):
		this._openingBg.rotation = openingBgTextTureRotation * (Math.PI / 180);

		let centerX = innerWidth / 2 - moveWidth / 2;
		let centerY = device.innerHeight / 2 - moveHeight / 2;

		this._openingBg.x = centerX + -1 * moveWidth * (this._actualProgress * 2) + moveX;
		this._openingBg.y = centerY;

		this._openingBg.alpha = 1 - this._actualProgress * 5;
	}

	//Hit areas
	//-------------------------------------------------------------------------------------

	/** Process checking hit area on Drag press */
	public processHitAreaMouseDown(): void {
		this._tiles.forEach((tile: WorkTilePixi) => {
			if (tile.isVisible) {
				tile.mouseDown(this._deps.getMouseCords());
			}
		});
	}

	public processHitAreaClick(cords: { x: number; y: number }): void {
		this._tiles.forEach((tile: WorkTilePixi) => {
			if (tile.isVisible) {
				tile.onClick(cords);
			}
		});
	}


	/** Process checking hit area on Drag press */
	public processHitAreaUpdate(): void {
		this._tiles.forEach((tile: WorkTilePixi) => {
			if (tile.isVisible && tile.hitAreaMouseDown) {
				tile.updateMouseCords(this._deps.getMouseCords());
			}
		});
	}

	//Tile passes
	//-------------------------------------------------------------------------------------

	/** Reset the all the tiles to start state */
	public resetTiles(): void {
		this._tiles.forEach((tile: WorkTilePixi) => tile.reset());
	}

	/** Hide all projects (APart from spacer tile and actiive index) */
	public hideAllProjects(skipIndex: number = -1): void {
		this._tiles.forEach((tile, index) => {
			if (index !== 0 && index !== skipIndex) {
				tile.hideForProject(index);
			}
		});
	}

	public resetAllProjects(skipIndex: number = -1): void {
		this._tiles.forEach((tile, index) => {
			if (index !== 0 && index !== skipIndex) {
				tile.resetForProject();
			}
		});
	}

	/** Show surrounding tiles again when zooming back out of a project */
	public showTilesFromProject(): void {
		this._tiles.forEach((tile: WorkTilePixi, index: number) => {
			if (index !== 0 && tile === this._activeProjectTile) {
				if (this._needsHardSizeReset) {
				}
			}

			if (index !== 0 && tile !== this._activeProjectTile) {
				if (this._needsHardSizeReset) {
					tile.hardReset();
				}

				tile.showFromProject(index);
			}
		});

		this._needsHardSizeReset = false;

		this._activeProjectTile = null;
	}

	/** Resize all the tiles (the active tile is skipped, matching the original) */
	public resizeTiles(): void {
		if (this._tiles) {
			this._tiles.forEach((tile) => {
				//Don't actually update actiive tile
				if (tile !== this._activeProjectTile) {
					tile.resize();
				}
			});
		}
	}

}
